import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChevronLeft, Calendar, FileText, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Parent-facing assessment history.
 *
 * Lists every completed assessment session that included at least one of
 * the parent's athletes, redacted to show only their own kids' data.
 *
 * One assessment → expandable card per athlete with the test results +
 * any coach commentary. Empty assessments are still shown so the parent
 * knows the session happened (just labeled "Notes captured in-person").
 */
export default function AssessmentHistory() {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/me/assessments`, { withCredentials: true });
                setRows(Array.isArray(r.data) ? r.data : []);
            } catch (e) {
                toast.error("Couldn't load assessments", { description: e?.response?.data?.detail });
            } finally {
                setBusy(false);
            }
        })();
    }, []);

    return (
        <div className="min-h-screen bg-black text-white">
            <div className="max-w-3xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
                <Link
                    to="/dashboard"
                    className="inline-flex items-center gap-1 text-[11px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors"
                    data-testid="assessment-history-back"
                >
                    <ChevronLeft className="w-3 h-3" />
                    Dashboard
                </Link>

                <div className="mt-4 mb-1 flex items-center gap-3">
                    <FileText className="w-5 h-5 text-brand-red" />
                    <h1 className="font-display text-3xl sm:text-4xl tracking-wider uppercase">Assessments</h1>
                </div>
                <p className="text-sm text-zinc-400 mb-8">
                    Every assessment we've run for your athlete(s). Each one is a snapshot —
                    keep these as benchmarks to track progress over time.
                </p>

                {busy ? (
                    <div className="flex items-center justify-center py-20 text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="border border-zinc-800 bg-zinc-950 px-6 py-12 text-center">
                        <p className="text-zinc-300 text-sm">No assessments yet.</p>
                        <p className="text-xs text-zinc-500 mt-2">
                            Once you've completed an Initial session, you'll see the snapshot here.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="assessment-history-list">
                        {rows.map((s) => <SessionCard key={s.session_id} session={s} />)}
                    </div>
                )}
            </div>
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-US", {
            weekday: "long", month: "long", day: "numeric", year: "numeric",
        });
    } catch { return iso; }
}

function SessionCard({ session }) {
    const [expanded, setExpanded] = useState(false);
    const parts = session.participants || [];
    const names = parts.map((p) => p.name).filter(Boolean).join(", ") || "Assessment";
    const totalRows = parts.reduce((acc, p) => acc + (p.rendered_results?.length || 0), 0);

    return (
        <div
            className="border border-zinc-800 bg-zinc-950 overflow-hidden"
            data-testid={`assessment-card-${session.session_id}`}
        >
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-zinc-900 transition-colors"
            >
                <Calendar className="w-4 h-4 text-zinc-500 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-0.5">
                        {formatDate(session.completed_at || session.started_at)}
                    </div>
                    <div className="text-sm text-white font-semibold truncate">{names}</div>
                    {session.started_by_name && (
                        <div className="text-[11px] text-zinc-500 mt-0.5">with {session.started_by_name}</div>
                    )}
                </div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 shrink-0">
                    {totalRows ? `${totalRows} test${totalRows === 1 ? "" : "s"}` : "Notes"}
                </div>
                <ChevronRight
                    className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
                />
            </button>

            {expanded && (
                <div className="border-t border-zinc-900 bg-black px-5 py-5 space-y-5">
                    {parts.map((p) => (
                        <AthleteBlock key={p.athlete_id || p.name} athlete={p} />
                    ))}
                </div>
            )}
        </div>
    );
}

function AthleteBlock({ athlete }) {
    const rows = athlete.rendered_results || [];
    const note = athlete.extra_notes || "";
    return (
        <div className="space-y-3">
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">Athlete</div>
            <h3 className="text-lg text-white font-semibold leading-tight">{athlete.name}</h3>
            {rows.length === 0 && !note ? (
                <p className="text-xs text-zinc-500 italic">
                    Notes captured in-person — no test values were recorded.
                </p>
            ) : (
                <table className="w-full border-collapse">
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.key} className="border-b border-zinc-900 last:border-b-0">
                                <td className="py-2 pr-4 text-[10px] tracking-[0.15em] uppercase text-zinc-400 font-mono w-2/5 align-top">
                                    {r.label}
                                </td>
                                <td className="py-2 text-sm text-white">{r.value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {note && (
                <div className="border-l-2 border-brand-red pl-3 mt-3 text-sm text-zinc-300 leading-relaxed">
                    {note}
                </div>
            )}
        </div>
    );
}
